import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party

import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/authentication/accountReducer';

// project imports

import axiosServices from 'utils/axios';
import Loader from 'ui-component/Loader';

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

const verifyToken = (accessToken) => {
    if (!accessToken) {
        return false;
    }
    const decoded = jwtDecode(accessToken);

    /**
     * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};
const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken);

        axiosServices.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
        localStorage.removeItem('accessToken');
        delete axiosServices.defaults.headers.common.Authorization;
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    useEffect(() => {
        const init = async () => {
            try {
                const accessToken = window.localStorage.getItem('accessToken');

                if (accessToken && verifyToken(accessToken)) {
                    setSession(accessToken);

                    const response = await axiosServices.get('/api/me');

                    const { user } = response.data;

                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user
                        }
                    });
                } else {
                    setSession(null);
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                console.error('Error in fetching /api/me:', err);
                setSession(null);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const login = async (email, password) => {
        const response = await axiosServices.post('/api/auth/login', { email, password });

        const { accessToken, refreshToken } = response.data.data;

        setSession(accessToken);

        localStorage.setItem('refreshToken', refreshToken);
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true
            }
        });
    };

    const logout = () => {
        setSession(null);
        dispatch({ type: LOGOUT });
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }
    return <JWTContext.Provider value={{ ...state, login, logout }}>{children}</JWTContext.Provider>;
};

JWTProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export default JWTContext;
